'use client';

import classNames from '@sindresorhus/class-names';
import {safeLocalStorage} from '@utils/safeStorages';
import DefaultAvatar from '@web-catalog/public/img/defaultAvatar.svg';
import VkIdLogo from '@web-catalog/public/img/logo_vkid.svg';
import Image from 'next/image';
import {useCallback, useEffect, useState} from 'react';

import {SNACKBAR_SHOW_TIME} from '@web-catalog/const/numbers';

import {useOmicronClientFlag} from '@web-catalog/hooks/omicron';

import {
  useAuthorizedProfile,
  useSetAuthProgressState,
} from '@web-catalog/store/auth';

import {useAuthAnalytics} from '@web-catalog/utils/analytics/useAuthAnalytics';

import {AUTH_SHOWN_STORAGE_KEY} from '../consts';
import styles from './AuthSnackBar.module.scss';

export const AuthSnackBar = () => {
  const webAvailableAutoLoginType = useOmicronClientFlag('WEB_AUTO_LOGIN');
  const userInfo = useAuthorizedProfile();
  const setAuthProgress = useSetAuthProgressState();
  const [isSnackBarVisible, setIsSnackBarVisible] = useState(false);
  const {
    logAuthorizationBannerShow,
    logAuthorizationDone,
    logAuthorizationBannerUserAgreementClick,
  } = useAuthAnalytics();

  const isAuthDisabled = webAvailableAutoLoginType === 'none';

  useEffect(() => {
    if (isAuthDisabled) return;

    if (userInfo?.id) {
      const authSnackBarShown = safeLocalStorage.getItem(
        AUTH_SHOWN_STORAGE_KEY,
      );

      if (!authSnackBarShown) {
        setIsSnackBarVisible(true);
        logAuthorizationBannerShow();
        logAuthorizationDone();
        safeLocalStorage.setItem(AUTH_SHOWN_STORAGE_KEY, 'shown');
      } else {
        setAuthProgress('finished');
      }
    }
  }, [isAuthDisabled, userInfo, setAuthProgress]);

  useEffect(() => {
    if (isAuthDisabled || !isSnackBarVisible) return;

    const showTimer = setTimeout(() => {
      setAuthProgress('finished');
      setIsSnackBarVisible(false);
    }, SNACKBAR_SHOW_TIME);

    return () => {
      clearTimeout(showTimer);
    };
  }, [isAuthDisabled, isSnackBarVisible, setAuthProgress]);

  const handleUserAgreementClick = useCallback(() => {
    logAuthorizationBannerUserAgreementClick();
  }, [logAuthorizationBannerUserAgreementClick]);

  if (isAuthDisabled || !userInfo?.id) {
    return null;
  }

  return (
    <div
      data-testid="auth-snack"
      className={classNames(styles.container, {
        [styles.hidden]: !isSnackBarVisible,
      })}
    >
      {userInfo?.photo_200 ? (
        <Image
          alt={userInfo?.first_name ?? 'User avatar'}
          src={userInfo?.photo_200}
          className={styles.avatar}
          width={36}
          height={36}
        />
      ) : (
        <DefaultAvatar className={styles.avatar} />
      )}

      <div>
        <div data-testid="name" className={styles.name}>{`Привет, ${
          userInfo?.first_name ?? userInfo?.id
        }!`}</div>
        <div data-testid="id" className={styles.id}>
          Вы вошли с помощью <VkIdLogo />
        </div>
        <a
          href={'https://www.rustore.ru/help/legal/terms-of-use/'}
          onClick={handleUserAgreementClick}
        >
          Пользовательское соглашение RuStore
        </a>
      </div>
    </div>
  );
};
